import Loader from "components/Loader/loader"
import React, { useEffect, useState } from "react"
import { FaPlus, FaEdit } from "react-icons/fa"
import { MdDelete, MdTimer } from "react-icons/md"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const daysOfWeek = [
  { name: "Monday", number: 1 },
  { name: "Tuesday", number: 2 },
  { name: "Wednesday", number: 3 },
  { name: "Thursday", number: 4 },
  { name: "Friday", number: 5 },
  { name: "Saturday", number: 6 },
  { name: "Sunday", number: 0 },
]

const calculateSlotHours = (start, end) => {
  if (!start || !end) return 0
  const [sh, sm] = start.split(":").map(Number)
  const [eh, em] = end.split(":").map(Number)

  let startDate = new Date(0, 0, 0, sh, sm)
  let endDate = new Date(0, 0, 0, eh, em)

  let diff = (endDate - startDate) / (1000 * 60 * 60)
  if (diff < 0) diff += 24

  return parseFloat(diff.toFixed(2))
}

const format12Hour = time24 => {
  if (!time24) return ""
  let [hour, minute] = time24.split(":").map(Number)
  const ampm = hour >= 12 ? "PM" : "AM"
  hour = hour % 12 || 12
  return `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")} ${ampm}`
}

// Helper: Convert "HH:MM" to minutes since midnight
const timeToMinutes = time => {
  const [h, m] = time.split(":").map(Number)
  return h * 60 + m
}

const AvailabilityTime = ({ isOpen, toggle, user }) => {
  const [loading, setLoading] = useState(false)
  const [slots, setSlots] = useState([])

  const [slotModal, setSlotModal] = useState({ open: false, dayNumber: null })
  const [newSlot, setNewSlot] = useState({ start: "00:00", end: "23:59" }) // Better defaults
  const [slotBeingEdited, setSlotBeingEdited] = useState(null)

  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    if (user?.id) {
      getUserSlots()
    }
  }, [user?.id])

  async function getUserSlots() {
    if (!user?.id) return
    setLoading(true)
    const APIdata = await fetchData(setLoading, `employee/slots/${user?.id}`)
    if (APIdata?.success) {
      setSlots(APIdata.slots || [])
    } else {
      Swal.fire({
        title: "Error",
        text: "Failed to load availability slots.",
        icon: "error",
      })
    }
    setLoading(false)
  }

  const openSlotModal = dayNumber => {
    setNewSlot({ start: "00:00", end: "23:59" })
    setSlotBeingEdited(null)
    setValidationErrors({})
    setSlotModal({ open: true, dayNumber })
  }

  const closeSlotModal = () => {
    setSlotModal({ open: false, dayNumber: null })
    setNewSlot({ start: "00:00", end: "23:59" })
    setSlotBeingEdited(null)
    setValidationErrors({})
  }

  // Validation function
  const validateSlot = () => {
    const errors = {}
    const { start, end } = newSlot
    const daySlots = slots.filter(s => s.day === slotModal.dayNumber)

    if (!start) {
      errors.start = "Start time is required"
    }
    if (!end) {
      errors.end = "End time is required"
    }

    if (start && end) {
      const startMin = timeToMinutes(start)
      const endMin = timeToMinutes(end)

      if (startMin >= endMin) {
        errors.end = "End time must be after start time"
      }

      // Check for overlap (excluding the slot being edited)
      const hasOverlap = daySlots.some(slot => {
        if (slotBeingEdited && slot.id === slotBeingEdited.id) return false

        const slotStart = timeToMinutes(slot.inTime)
        const slotEnd = timeToMinutes(slot.outTime)

        return (
          startMin < slotEnd && endMin > slotStart // Overlap condition
        )
      })

      if (hasOverlap) {
        errors.general = "This time slot overlaps with an existing slot"
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSlotSave() {
    if (!validateSlot()) {
      return
    }

    setLoading(true)

    const payload = {
      employeeId: user.id,
      day: slotModal.dayNumber,
      inTime: newSlot.start,
      outTime: newSlot.end,
    }

    try {
      let APIdata
      if (slotBeingEdited) {
        APIdata = await FetchPutData(
          setLoading,
          "employee/slots",
          slotBeingEdited.id,
          payload
        )
      } else {
        APIdata = await FetchPostData(setLoading, "employee/slots", payload)
      }

      if (APIdata?.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: slotBeingEdited ? "Slot updated!" : "Slot added!",
          timer: 1500,
        })
        getUserSlots()
        setNewSlot({ start: "00:00", end: "23:59" })
        setSlotBeingEdited(null)
        setValidationErrors({})
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: APIdata?.response?.data?.message || "Something went wrong",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred",
      })
    } finally {
      setLoading(false)
    }
  }

  async function deleteSlot(slotId) {
    const result = await Swal.fire({
      title: "Delete Slot?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    })

    if (result.isConfirmed) {
      setLoading(true)
      const APIdata = await FetchDeleteData(
        setLoading,
        "employee/slots",
        slotId
      )
      if (APIdata?.data?.success) {
        Swal.fire("Deleted!", "", "success")
        getUserSlots()
      } else {
        Swal.fire("Error", "Failed to delete slot", "error")
      }
      setLoading(false)
    }
  }

  const editSlot = slot => {
    setNewSlot({ start: slot.inTime, end: slot.outTime })
    setSlotBeingEdited(slot)
    setValidationErrors({})
  }

  return (
    <>
      {loading && <Loader />}

      {/* Main Availability Modal */}
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle}>
          Set Availability for <strong>{user?.name || "..."}</strong>
        </ModalHeader>
        <ModalBody>
          <Table bordered responsive className="text-center bg-white">
            <thead className="bg-blue text-white">
              <tr>
                <th>Day</th>
                <th>Slots</th>
                <th>Total Hours</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {daysOfWeek.map(day => {
                const daySlots = slots.filter(s => s.day === day.number)
                const totalHours = daySlots
                  .reduce(
                    (total, s) =>
                      total + calculateSlotHours(s.inTime, s.outTime),
                    0
                  )
                  .toFixed(2)

                return (
                  <tr key={day.number}>
                    <td className="fw-bold">{day.name}</td>
                    <td>
                      {daySlots.length > 0 ? (
                        daySlots.map(slot => (
                          <div key={slot.id}>
                            {format12Hour(slot.inTime)} -{" "}
                            {format12Hour(slot.outTime)}
                          </div>
                        ))
                      ) : (
                        <span className="text-muted">No slots</span>
                      )}
                    </td>
                    <td>{totalHours} hrs</td>
                    <td>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => openSlotModal(day.number)}
                      >
                        <MdTimer size={14} className="me-1" />
                        Manage Slots
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-cancel" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Slot Management Modal */}
      <Modal isOpen={slotModal.open} toggle={closeSlotModal} centered size="md">
        <ModalHeader toggle={closeSlotModal}>
          Manage Slots –{" "}
          <strong>
            {daysOfWeek.find(d => d.number === slotModal.dayNumber)?.name}
          </strong>
        </ModalHeader>

        <ModalBody>
          {/* Add/Edit Slot Form - Symmetrical Layout */}
          <div className="p-3 border rounded bg-light mb-4">
            <Row className="align-items-start g-3">
              <Col md={5}>
                <FormGroup>
                  <Label className="fw-medium">Start Time</Label>
                  <Input
                    type="time"
                    value={newSlot.start}
                    onChange={e =>
                      setNewSlot(prev => ({ ...prev, start: e.target.value }))
                    }
                    invalid={!!validationErrors.start}
                  />
                  {validationErrors.start && (
                    <small className="text-danger">
                      {validationErrors.start}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md={5}>
                <FormGroup>
                  <Label className="fw-medium">End Time</Label>
                  <Input
                    type="time"
                    value={newSlot.end}
                    onChange={e =>
                      setNewSlot(prev => ({ ...prev, end: e.target.value }))
                    }
                    invalid={!!validationErrors.end}
                  />
                  {validationErrors.end && (
                    <small className="text-danger">
                      {validationErrors.end}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md={2}>
                <Button
                  color="success"
                  className="w-100 h-100  mt-4"
                  onClick={handleSlotSave}
                  disabled={loading}
                >
                  {slotBeingEdited ? (
                    <>
                      <FaEdit className="mb-1" />
                    </>
                  ) : (
                    <>
                      <FaPlus className="mb-1" />
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {/* General validation error (e.g., overlap) */}
            {validationErrors.general && (
              <div className="alert alert-danger mt-3 small py-2" role="alert">
                {validationErrors.general}
              </div>
            )}
          </div>

          {/* Existing Slots List */}
          <h6 className="mb-3 text-muted border-bottom pb-2">Existing Slots</h6>

          {slots.filter(s => s.day === slotModal.dayNumber).length === 0 ? (
            <p className="text-center text-muted py-4">
              No availability slots added yet for this day.
            </p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {slots
                .filter(s => s.day === slotModal.dayNumber)
                .map(slot => (
                  <div
                    key={slot.id}
                    className="d-flex justify-content-between align-items-center p-3 border rounded shadow-sm bg-white hover-shadow"
                    style={{ transition: "all 0.2s" }}
                  >
                    <div className="fw-medium">
                      {format12Hour(slot.inTime)} – {format12Hour(slot.outTime)}
                      <small className="text-muted ms-2">
                        ({calculateSlotHours(slot.inTime, slot.outTime)} hrs)
                      </small>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => editSlot(slot)}
                        outline
                      >
                        <FaEdit size={14} />
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => deleteSlot(slot.id)}
                        outline
                      >
                        <MdDelete size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-0">
          <Button color="secondary" onClick={closeSlotModal}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default AvailabilityTime
